import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet, Users, Receipt } from "lucide-react";
import { api, formatApiError, getActiveBranchName } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { BranchFilter } from "@/components/reports/BranchFilter";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { money } from "@/lib/constants";
import { ShareExportPanel } from "@/components/reports/ShareExportPanel";

const PAYMENT_METHODS = ["efectivo", "transferencia", "cheque", "tarjeta", "otro"];

export default function PagosProveedores() {
  const { isSuperAdmin, activeTenantId, branches } = useTenant();

  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [supplierId, setSupplierId] = useState("all");
  const [branchId, setBranchId] = useState("all");
  const [method, setMethod] = useState("all");

  const tenantParam = useMemo(
    () => (isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {}),
    [isSuperAdmin, activeTenantId],
  );

  const params = useMemo(() => ({
    ...tenantParam,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    supplier_id: supplierId !== "all" ? supplierId : undefined,
    branch_id: branchId !== "all" ? branchId : undefined,
    payment_method: method !== "all" ? method : undefined,
  }), [tenantParam, dateFrom, dateTo, supplierId, branchId, method]);

  async function fetchData() {
    setLoading(true);
    try {
      const [{ data }, { data: sup }] = await Promise.all([
        api.get("/reports/supplier-payments", { params }),
        api.get("/suppliers", { params: { ...tenantParam, include_inactive: true } }),
      ]);
      setItems(data.items || []);
      setTotals(data.totals || {});
      setSuppliers(sup);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchData, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, supplierId, branchId, method, activeTenantId]);

  return (
    <div className="space-y-6" data-testid="page-supplier-payments">
      <PageHeader
        title="Pagos a proveedores"
        description="Historial de pagos registrados sobre cuentas por pagar. Filtra por proveedor, sucursal y periodo."
        actions={
          <ShareExportPanel
            reportKey="supplier_payments"
            reportName="Pagos a proveedores"
            endpoint="/reports/supplier-payments"
            params={params}
            exportPermission="accounts_payable:export"
            filename="pagos-a-proveedores"
            branchId={branchId !== "all" ? branchId : null}
            filters={{
              date_from: dateFrom || null,
              date_to: dateTo || null,
              supplier_id: supplierId !== "all" ? supplierId : null,
              branch_name: getActiveBranchName(),
              payment_method: method !== "all" ? method : null,
            }}
            stats={{
              payments_count: totals.payments_count,
              total_paid: totals.total_paid,
              distinct_suppliers: totals.distinct_suppliers,
              row_count: items.length,
            }}
            testIdPrefix="sp-share"
          />
        }
      />

      <Card className="border-border">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="sp-date-from" placeholder="Desde" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="sp-date-to" placeholder="Hasta" />
          <Select value={supplierId} onValueChange={setSupplierId}>
            <SelectTrigger data-testid="sp-supplier"><SelectValue placeholder="Proveedor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.trade_name || s.legal_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <BranchFilter />
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="sp-method"><SelectValue placeholder="Método" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {PAYMENT_METHODS.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border" data-testid="sp-kpi-count">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Receipt className="h-3.5 w-3.5" /> Pagos
            </div>
            <div className="text-2xl font-semibold mono mt-1">{totals.payments_count || 0}</div>
          </CardContent>
        </Card>
        <Card className="border-border" data-testid="sp-kpi-total">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" /> Total pagado
            </div>
            <div className="text-2xl font-semibold mono mt-1">{money(totals.total_paid)}</div>
          </CardContent>
        </Card>
        <Card className="border-border" data-testid="sp-kpi-suppliers">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Users className="h-3.5 w-3.5" /> Proveedores
            </div>
            <div className="text-2xl font-semibold mono mt-1">{totals.distinct_suppliers || 0}</div>
          </CardContent>
        </Card>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="Sin pagos" description="No hay pagos que coincidan con los filtros actuales." icon={Wallet} />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>OC</TableHead>
                <TableHead>Método</TableHead>
                <TableHead>Referencia</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead>Registró</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`sp-row-${p.id}`} className="tbl-row">
                  <TableCell className="text-sm">{p.payment_date}</TableCell>
                  <TableCell className="text-sm">{p.supplier_name}</TableCell>
                  <TableCell className="mono text-xs">{p.po_folio}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{p.payment_method}</Badge></TableCell>
                  <TableCell className="mono text-xs">{p.reference}</TableCell>
                  <TableCell className="text-right mono">{money(p.amount)}</TableCell>
                  <TableCell className="text-sm">{p.created_by_name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
